import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { formatGrade } from "@lib/intl"
import { Eye, Sparkles, Target, TriangleAlert } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"

import type { DirectionInsight, InsightKind } from "../types"

interface Props {
  insights: DirectionInsight[]
}

const KIND_TONE: Record<InsightKind, string> = {
  win: "border-success bg-success-soft",
  alert: "border-danger bg-danger-soft",
  milestone: "border-accent bg-accent-soft",
  dormant: "border-attention bg-attention-soft",
}

const KIND_ICON_TONE: Record<InsightKind, string> = {
  win: "text-success",
  alert: "text-danger-ink",
  milestone: "text-accent-ink",
  dormant: "text-attention-ink",
}

function IconFor({ kind }: { kind: InsightKind }) {
  const className = cn("h-4 w-4", KIND_ICON_TONE[kind])
  if (kind === "win") return <Sparkles className={className} aria-hidden="true" />
  if (kind === "alert") return <TriangleAlert className={className} aria-hidden="true" />
  if (kind === "milestone") return <Target className={className} aria-hidden="true" />
  return <Eye className={className} aria-hidden="true" />
}

export const InsightsColumn = memo(function InsightsColumn({ insights }: Props) {
  const { t } = useTranslation("direction")
  const scale = useGradeScale()

  return (
    <aside aria-label={t("insights.section_label")} className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-ink text-sm font-semibold">{t("insights.title")}</h2>
        <span className="text-ink-subtle text-xs">{t("insights.subtitle")}</span>
      </div>
      <ul className="space-y-2">
        {insights.map((insight) => {
          // Each kind has a fixed `body_<kind>` key whose params are
          // exactly the union member's payload. Localized translation
          // resolves the right message + interpolation.
          const body =
            insight.kind === "win"
              ? t("insights.body_win", {
                  classroom_name: insight.payload.classroom_name,
                  subject: insight.payload.subject,
                  delta: formatGrade(insight.payload.delta, scale),
                })
              : insight.kind === "alert"
                ? t("insights.body_alert", { count: insight.payload.count })
                : insight.kind === "milestone"
                  ? t("insights.body_milestone", { percent: insight.payload.percent })
                  : t("insights.body_dormant", {
                      professor_name: insight.payload.professor_name,
                      days: insight.payload.days,
                    })
          return (
            <li key={insight.id}>
              <article
                className={cn(
                  "rounded-lg border p-3 text-sm shadow-sm transition-colors",
                  KIND_TONE[insight.kind],
                )}
              >
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0">
                    <IconFor kind={insight.kind} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-ink font-medium">{t(`insights.title_${insight.kind}`)}</p>
                    <p className="text-ink-muted mt-0.5 text-sm leading-snug">{body}</p>
                  </div>
                </div>
              </article>
            </li>
          )
        })}
      </ul>
    </aside>
  )
})
